import { motion } from "framer-motion";
import { Megaphone, Clapperboard, Crosshair, PanelsTopLeft, HeartHandshake, Telescope } from "lucide-react";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

/* ---------------- How We Work ---------------- */
const STEPS = [
  { n: "01", title: "Audit",    text: "Funnel, tracking, creatives and account structure reviewed. You get a clear gap list." },
  { n: "02", title: "Setup",    text: "Pixel, CAPI, postbacks, audiences and the creative pipeline live before a dollar is spent." },
  { n: "03", title: "Launch",   text: "Structured launch with safer UGC creatives and allowed-state targeting." },
  { n: "04", title: "Optimize", text: "Weekly creative rotation and budget decisions driven by FTD value — not CPMs." },
  { n: "05", title: "Scale",    text: "Winners get budget, losers get cut. Scaling follows deposit data, not the calendar." },
];

export default function ProcessSection() {
  return (
    <section id="how-we-work" className="py-20 md:py-28 bg-[#FAFAF7] scroll-mt-20" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl mb-12 md:mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7C3AED] font-heading mb-4 md:mb-5">How we work</p>
          <h2 className="font-heading text-[1.75rem] sm:text-4xl lg:text-[2.75rem] font-bold text-[#0A0A0A] leading-[1.15]">
            How we scale sweepstakes campaigns: audit, setup, launch, optimize, scale.
          </h2>
          <p className="text-[15px] md:text-lg text-[#525252] mt-4 md:mt-5 leading-relaxed">
            One playbook, five stages. Every step is a checkpoint against FTDs, deposits and LTV.
          </p>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="relative h-full bg-white border border-[#E7E5E4] rounded-2xl p-5 md:p-6 hover:border-[#7C3AED]/30 hover:shadow-[0_10px_30px_-15px_rgba(124,58,237,0.2)] transition-all duration-300" data-testid={`process-step-${i + 1}`}>
                <div className="flex items-center gap-2 mb-4">
                  <span className="font-heading text-sm font-bold text-[#7C3AED] tabular-nums">{s.n}</span>
                  <span className="h-px flex-1 bg-[#E7E5E4]" />
                </div>
                <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mb-2.5">{s.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Services ---------------- */
const SERVICES = [
  { icon: Megaphone,     title: "Paid Social Acquisition",    text: "Meta and TikTok campaigns managed end-to-end for player acquisition." },
  { icon: Clapperboard,  title: "Creative Production",        text: "UGC, statics and fast iterations produced weekly for the sweepstakes category." },
  { icon: Crosshair,     title: "Tracking & Attribution",     text: "Pixel, CAPI and postback setup connecting ad spend to registrations and deposits." },
  { icon: PanelsTopLeft, title: "Landing Page Strategy",      text: "Pre-landers and funnels aligned with your offers and compliance requirements." },
  { icon: HeartHandshake, title: "CRM & Retention Support",   text: "Email and SMS flows that turn registrants into depositors and repeat players." },
  { icon: Telescope,     title: "Market & Competitor Research", text: "Offer, creative and funnel intelligence gathered from across the vertical." },
];

export function ServicesSection() {
  return (
    <section id="services" className="py-20 md:py-28 bg-white border-y border-[#E7E5E4] scroll-mt-20" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl mb-12 md:mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7C3AED] font-heading mb-4 md:mb-5">Services</p>
          <h2 className="font-heading text-[1.75rem] sm:text-4xl lg:text-[2.75rem] font-bold text-[#0A0A0A] leading-[1.15]">
            Sweepstakes marketing services — everything acquisition needs.
          </h2>
          <p className="text-[15px] md:text-lg text-[#525252] mt-4 md:mt-5 leading-relaxed">
            Six disciplines. One team. One dashboard from ad account to depositor.
          </p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.08}>
              <div
                className="h-full bg-[#FAFAF7] border border-[#E7E5E4] rounded-2xl p-6 md:p-7 hover:border-[#7C3AED]/30 hover:bg-white transition-all duration-300"
                data-testid={`service-card-${i + 1}`}
              >
                <div className="w-11 h-11 rounded-xl bg-white border border-[#E7E5E4] flex items-center justify-center text-[#7C3AED] mb-5 shadow-sm">
                  <s.icon size={20} strokeWidth={1.75} />
                </div>
                <h3 className="font-heading text-base font-bold text-[#0A0A0A] mb-2.5">{s.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
